/* eslint-disable react/no-unknown-property */
import { useFrame, useThree } from '@react-three/fiber'
import React, { forwardRef, useImperativeHandle } from 'react'
import * as THREE from 'three'

import { useExcludeFromReflections } from './reflector-gate'

export interface TargetPuckRef {
  getPuckPosition: () => THREE.Vector3 | undefined
  hidePuck: (arg: boolean) => void
}

interface TargetPuckProps {
  targetSurface: React.RefObject<THREE.Mesh>
  ring?: [number, number, number] | undefined
}

const TargetPuck = forwardRef<TargetPuckRef | undefined, TargetPuckProps>(
  ({ targetSurface, ring }, ref) => {
    const TARGET_PUCK_NAME = 'target-puck'

    const followerRef = React.useRef<THREE.Mesh>(null)

    const [isVisible, setIsVisible] = React.useState(true)

    const { raycaster, camera, pointer } = useThree()

    const intersectPoint = React.useMemo(() => new THREE.Vector3(), [])

    useExcludeFromReflections(followerRef)

    const hidePuck = (arg: boolean) => setIsVisible(!arg)

    useImperativeHandle(ref, () => ({
      getPuckPosition: () => followerRef.current?.position,
      hidePuck,
    }))

    const isHitBoxWall = (name: string) => /wall/i.test(name)

    const getTargetIntersects = (
      scene: THREE.Scene,
      followerMesh: THREE.Mesh
    ) =>
      raycaster.intersectObjects(scene.children, true).filter((hit) => {
        if (hit.object !== followerMesh) {
          if (!isHitBoxWall(hit.object.name)) {
            return false
          }
          return hit.object.name !== TARGET_PUCK_NAME
        }
        return false
      })

    const checkIfBlocked = (
      firstHit: THREE.Intersection,
      targetIntersect: THREE.Intersection,
      targetMesh: THREE.Mesh
    ) => {
      if (firstHit) {
        return (
          firstHit.distance < targetIntersect.distance &&
          firstHit.object !== targetMesh
        )
      }
      return false
    }

    useFrame(({ scene }) => {
      const targetMesh = targetSurface.current
      const followerMesh = followerRef.current

      if (!targetMesh || !followerMesh) return

      raycaster.setFromCamera(pointer, camera)

      const [firstHit] = getTargetIntersects(scene, followerMesh)

      const [targetIntersect] = raycaster.intersectObject(targetMesh, false)

      if (targetIntersect && isVisible) {
        const isBlocked = checkIfBlocked(firstHit, targetIntersect, targetMesh)
        if (isBlocked) {
          followerMesh.visible = false
          return
        }
        followerMesh.visible = true
        intersectPoint.copy(targetIntersect.point)
        const normal = targetIntersect.face!.normal.clone().normalize()
        intersectPoint.add(normal.multiplyScalar(0.05))
        followerMesh.position.copy(intersectPoint)
        followerMesh.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 0, 20),
          normal
        )
        return
      }
      followerMesh.visible = false
    })

    return (
      <mesh ref={followerRef} name={TARGET_PUCK_NAME}>
        <ringGeometry args={ring || [0.4, 0.44, 50]} />
        <meshStandardMaterial
          color="white"
          emissive="yellow"
          emissiveIntensity={10}
        />
      </mesh>
    )
  }
)

export default TargetPuck
